import { useState } from 'react';

const categories = [
  {
    id: 'higienicos',
    name: 'Higiénicos',
    icon: 'ri-file-paper-2-line',
    color: 'from-blue-50 to-slate-50',
  },
  {
    id: 'quimicos',
    name: 'Químicos Industriales',
    icon: 'ri-flask-line',
    color: 'from-blue-50 to-slate-50',
  },
  {
    id: 'alcoholes',
    name: 'Alcoholes y Solventes',
    icon: 'ri-drop-line',
    color: 'from-blue-50 to-slate-50',
  },
  {
    id: 'mezclas',
    name: 'Mezclas Personalizadas',
    icon: 'ri-test-tube-line',
    color: 'from-blue-50 to-slate-50',
  },
  {
    id: 'industriales',
    name: 'Suministros Industriales',
    icon: 'ri-tools-line',
    color: 'from-blue-50 to-slate-50',
  },
] as const;

type CategoryId = typeof categories[number]['id'];

const products: Record<
  CategoryId,
  {
    name: string;
    description: string;
    benefit: string;
    application: string;
    icon: string;
    image: string;
  }[]
> = {
  higienicos: [
    {
      name: 'Papel Higiénico Tradicional',
      description:
        'Papel higiénico de alta calidad con suavidad y resistencia óptimas para instalaciones de alto tráfico.',
      benefit: 'Reduce costos operativos con mayor rendimiento por rollo',
      application: 'Hoteles, oficinas corporativas, centros comerciales',
      icon: 'ri-file-paper-line',
      image:
        'https://public.readdy.ai/ai/img_res/b3bb33a73a59f6992fdf6b67ef751e5c.jpg',
    },
    {
      name: 'Toalla en Rollo 160 y 180 mts',
      description:
        'Toalla en rollo de gran metraje diseñada para dispensadores de alto rendimiento en entornos empresariales.',
      benefit: 'Menor frecuencia de reposición, mayor eficiencia operativa',
      application: 'Plantas industriales, hospitales, restaurantes',
      icon: 'ri-file-list-3-line',
      image:
        'https://readdy.ai/api/search-image?query=large%20industrial%20paper%20towel%20roll%20white%20clean%20on%20white%20surface%2C%20professional%20studio%20lighting%2C%20minimalist%20corporate%20product%20photography%20premium%20hygiene%20supply%20elegant%20presentation%20no%20people&width=600&height=400&seq=hig-toalla-002&orientation=landscape',
    },
    {
      name: 'Toalla en Rollo y Papel Fluido Céntrico TAD',
      description:
        'Toalla en rollo y papel fluido con extracción céntrica, disponibles en diferentes metrajes. Fabricados con tecnología TAD (Through Air Drying) que ofrece máxima suavidad, absorción superior y resistencia excepcional.',
      benefit: 'Calidad premium que genera ahorros considerables: mayor absorción con menos consumo por uso',
      application: 'Hospitales, hoteles de lujo, oficinas corporativas, industria alimenticia',
      icon: 'ri-hand-heart-line',
      image:
        'https://static.readdy.ai/image/31562a2a45c0a727692c38b1929c3137/d0eb2242da6185661a975f5d5a1648bd.png',
    },
    {
      name: 'Papel Jumbo 360 a 600 mts',
      description:
        'Solución de máximo rendimiento para instalaciones de gran afluencia. Hasta 600 metros por rollo.',
      benefit: 'Hasta 4x más rendimiento que el papel convencional',
      application:
        'Aeropuertos, centros de convenciones, industria manufacturera',
      icon: 'ri-file-copy-2-line',
      image:
        'https://readdy.ai/api/search-image?query=jumbo%20toilet%20paper%20roll%20extra%20large%20industrial%20size%20white%20clean%20on%20white%20background%2C%20professional%20studio%20lighting%20minimalist%20corporate%20product%20photography%20premium%20supply%20elegant%20presentation&width=600&height=400&seq=hig-jumbo-003&orientation=landscape',
    },
    {
      name: 'Toalla Interdoblada Ecológica',
      description:
        'Toalla de hoja individual con certificación ecológica. Compatible con dispensadores de palanca y automáticos.',
      benefit: 'Reduce el desperdicio hasta un 30% vs. toalla en rollo',
      application:
        'Oficinas corporativas, sector salud, sector gubernamental',
      icon: 'ri-leaf-line',
      image:
        'https://readdy.ai/api/search-image?query=eco-friendly%20interfolded%20paper%20towels%20stack%20white%20clean%20on%20white%20marble%20surface%2C%20professional%20studio%20lighting%20minimalist%20corporate%20product%20photography%20sustainable%20hygiene%20supply%20elegant%20presentation&width=600&height=400&seq=hig-eco-004&orientation=landscape',
    },
    {
      name: 'Servilletas Tradicionales',
      description:
        'Servilletas de mesa de alta calidad con acabado suave y resistente. Disponibles en diferentes presentaciones para todo tipo de establecimiento.',
      benefit: 'Excelente relación calidad-precio con presentación profesional',
      application: 'Restaurantes, hoteles, comedores industriales, cafeterías',
      icon: 'ri-layout-masonry-line',
      image:
        'https://readdy.ai/api/search-image?query=stack%20of%20white%20traditional%20paper%20napkins%20neatly%20arranged%20on%20clean%20white%20marble%20surface%2C%20professional%20studio%20lighting%20minimalist%20corporate%20product%20photography%20premium%20restaurant%20supply%20elegant%20presentation%20no%20people&width=600&height=400&seq=hig-servilletas-006&orientation=landscape',
    },
  ],
  quimicos: [
    {
      name: 'Limpiadores para Superficies Industriales',
      description:
        'Formulaciones de alta concentración para la limpieza profunda de superficies en entornos industriales exigentes.',
      benefit:
        'Elimina contaminantes industriales con una sola aplicación',
      application: 'Plantas manufactureras, talleres, almacenes',
      icon: 'ri-bubble-chart-line',
      image:
        'https://readdy.ai/api/search-image?query=industrial%20cleaning%20chemical%20products%20bottles%20professional%20white%20background%2C%20studio%20lighting%20minimalist%20corporate%20product%20photography%20premium%20chemical%20supply%20elegant%20presentation%20no%20people&width=600&height=400&seq=qui-limpiador-001&orientation=landscape',
    },
    {
      name: 'Desengrasantes Industriales',
      description:
        'Soluciones de alto poder para la remoción de grasas, aceites y residuos en maquinaria y equipos.',
      benefit: 'Prolonga la vida útil de equipos y maquinaria',
      application: 'Industria automotriz, manufactura, cocinas industriales',
      icon: 'ri-contrast-drop-2-line',
      image:
        'https://readdy.ai/api/search-image?query=industrial%20degreaser%20chemical%20cleaner%20bottle%20professional%20white%20background%2C%20studio%20lighting%20minimalist%20corporate%20product%20photography%20premium%20industrial%20supply%20elegant%20presentation&width=600&height=400&seq=qui-desengrasante-002&orientation=landscape',
    },
    {
      name: 'Desinfectantes Hospitalarios',
      description:
        'Productos con certificación para uso en entornos de salud. Eliminan el 99.99% de patógenos.',
      benefit: 'Cumplimiento normativo para instalaciones de salud',
      application: 'Hospitales, clínicas, laboratorios, sector salud',
      icon: 'ri-heart-pulse-line',
      image:
        'https://readdy.ai/api/search-image?query=hospital%20grade%20disinfectant%20spray%20bottle%20professional%20white%20background%2C%20studio%20lighting%20minimalist%20corporate%20product%20photography%20premium%20medical%20supply%20elegant%20presentation%20no%20people&width=600&height=400&seq=qui-hospital-003&orientation=landscape',
    },
    {
      name: 'Detergentes de Alta Concentración',
      description:
        'Detergentes industriales de alta concentración para lavado de ropa, pisos y equipos en volumen.',
      benefit:
        'Rendimiento superior: 1 litro equivale a 10 litros convencionales',
      application: 'Lavanderías industriales, hoteles, restaurantes',
      icon: 'ri-drop-fill',
      image:
        'https://readdy.ai/api/search-image?query=concentrated%20industrial%20detergent%20large%20container%20professional%20white%20background%2C%20studio%20lighting%20minimalist%20corporate%20product%20photography%20premium%20cleaning%20supply%20elegant%20presentation&width=600&height=400&seq=qui-detergente-004&orientation=landscape',
    },
    {
      name: 'Sosa en Escamas',
      description:
        'Hidróxido de sodio en presentación de escamas de alta pureza. Utilizado en procesos de limpieza profunda, tratamiento de aguas, fabricación de jabones y múltiples aplicaciones industriales.',
      benefit: 'Alta pureza y disolución rápida para procesos industriales eficientes',
      application: 'Industria alimenticia, tratamiento de aguas, manufactura, sector químico',
      icon: 'ri-fire-line',
      image:
        'https://readdy.ai/api/search-image?query=sodium%20hydroxide%20caustic%20soda%20flakes%20white%20chemical%20in%20industrial%20container%20on%20clean%20white%20background%2C%20professional%20studio%20lighting%20minimalist%20corporate%20product%20photography%20premium%20chemical%20supply%20elegant%20presentation%20no%20people&width=600&height=400&seq=qui-sosa-005&orientation=landscape',
    },
    {
      name: 'Bifluoruro de Amonio',
      description:
        'Compuesto químico especializado para limpieza de metales, remoción de óxido, grabado de vidrio y tratamiento de superficies industriales. Alta efectividad en aplicaciones técnicas.',
      benefit: 'Limpieza y tratamiento de superficies metálicas con resultados profesionales',
      application: 'Industria metalúrgica, vidriería, manufactura, mantenimiento industrial',
      icon: 'ri-shield-flash-line',
      image:
        'https://readdy.ai/api/search-image?query=ammonium%20bifluoride%20industrial%20chemical%20powder%20in%20professional%20container%20on%20clean%20white%20background%2C%20professional%20studio%20lighting%20minimalist%20corporate%20product%20photography%20premium%20chemical%20supply%20elegant%20presentation%20no%20people&width=600&height=400&seq=qui-bifluoruro-006&orientation=landscape',
    },
    {
      name: 'Químicos de Manufactura en General',
      description:
        'Amplia gama de productos químicos para procesos de manufactura: ácidos, bases, solventes y reactivos industriales adaptados a las necesidades de cada línea de producción.',
      benefit: 'Suministro integral de químicos para toda tu operación manufacturera',
      application: 'Plantas de manufactura, industria automotriz, farmacéutica, textil',
      icon: 'ri-flask-fill',
      image:
        'https://readdy.ai/api/search-image?query=various%20industrial%20manufacturing%20chemicals%20bottles%20and%20containers%20arranged%20professionally%20on%20clean%20white%20background%2C%20professional%20studio%20lighting%20minimalist%20corporate%20product%20photography%20premium%20chemical%20supply%20elegant%20presentation%20no%20people&width=600&height=400&seq=qui-manufactura-007&orientation=landscape',
    },
  ],
  alcoholes: [
    {
      name: 'Alcohol en Gel al 75%',
      description:
        'Gel antibacterial de grado industrial con 75% de alcohol isopropílico. Fórmula con humectantes para uso frecuente.',
      benefit:
        'Protección certificada sin resecar la piel en uso continuo',
      application: 'Oficinas, hospitales, restaurantes, comercios',
      icon: 'ri-hand-sanitizer-line',
      image:
        'https://readdy.ai/api/search-image?query=hand%20sanitizer%20gel%20bottle%2075%20percent%20alcohol%20professional%20white%20background%2C%20studio%20lighting%20minimalist%20corporate%20product%20photography%20premium%20hygiene%20supply%20elegant%20presentation%20no%20people&width=600&height=400&seq=alc-gel-001&orientation=landscape',
    },
    {
      name: 'Alcohol Isopropílico Industrial',
      description:
        'Alcohol isopropílico de alta pureza para limpieza de equipos electrónicos, ópticos y superficies sensibles.',
      benefit:
        'Evaporación rápida sin residuos, ideal para equipos delicados',
      application:
        'Industria electrónica, laboratorios, manufactura de precisión',
      icon: 'ri-flask-line',
      image:
        'https://readdy.ai/api/search-image?query=isopropyl%20alcohol%20industrial%20bottle%20professional%20white%20background%2C%20studio%20lighting%20minimalist%20corporate%20product%20photography%20premium%20chemical%20supply%20elegant%20presentation&width=600&height=400&seq=alc-iso-002&orientation=landscape',
    },
    {
      name: 'Solventes para Mantenimiento',
      description:
        'Solventes industriales para limpieza de maquinaria, remoción de adhesivos y mantenimiento preventivo.',
      benefit:
        'Compatibles con metales, plásticos y superficies pintadas',
      application:
        'Mantenimiento industrial, automotriz, manufactura',
      icon: 'ri-oil-line',
      image:
        'https://readdy.ai/api/search-image?query=industrial%20solvent%20maintenance%20chemical%20container%20professional%20white%20background%2C%20studio%20lighting%20minimalist%20corporate%20product%20photography%20premium%20industrial%20supply%20elegant%20presentation&width=600&height=400&seq=alc-solvente-003&orientation=landscape',
    },
    {
      name: 'Alcohol Etílico Desnaturalizado',
      description:
        'Alcohol etílico para limpieza general, desinfección de superficies y procesos industriales de sanitización.',
      benefit:
        'Versatilidad de aplicación en múltiples sectores industriales',
      application:
        'Industria alimenticia, farmacéutica, sector salud',
      icon: 'ri-test-tube-line',
      image:
        'https://readdy.ai/api/search-image?query=denatured%20ethyl%20alcohol%20bottle%20professional%20white%20background%2C%20studio%20lighting%20minimalist%20corporate%20product%20photography%20premium%20chemical%20supply%20elegant%20presentation%20no%20people&width=600&height=400&seq=alc-etilico-004&orientation=landscape',
    },
  ],
  mezclas: [
    {
      name: 'Desarrollo de Fórmulas a Medida',
      description:
        'Creamos formulaciones químicas personalizadas según los requerimientos específicos de tu proceso industrial.',
      benefit: 'Solución exacta para tu proceso, sin compromisos',
      application:
        'Industria manufacturera, farmacéutica, alimenticia',
      icon: 'ri-microscope-line',
      image:
        'https://readdy.ai/api/search-image?query=chemical%20laboratory%20custom%20formula%20development%20professional%20white%20background%2C%20studio%20lighting%20minimalist%20corporate%20product%20photography%20premium%20industrial%20research%20elegant%20presentation%20no%20people&width=600&height=400&seq=mez-formula-001&orientation=landscape',
    },
    {
      name: 'Adaptación a Procesos Industriales',
      description:
        'Ajustamos concentraciones, pH, viscosidad y propiedades de los productos para integrarse a tus líneas de producción.',
      benefit:
        'Integración perfecta sin modificar tus procesos actuales',
      application: 'Líneas de producción, plantas de manufactura',
      icon: 'ri-settings-3-line',
      image:
        'https://readdy.ai/api/search-image?query=industrial%20process%20chemical%20adaptation%20mixing%20professional%20white%20background%2C%20studio%20lighting%20minimalist%20corporate%20product%20photography%20premium%20manufacturing%20supply%20elegant%20presentation&width=600&height=400&seq=mez-proceso-002&orientation=landscape',
    },
    {
      name: 'Mezclas para Sector Salud',
      description:
        'Formulaciones especializadas que cumplen con normativas sanitarias para hospitales, clínicas y laboratorios.',
      benefit:
        'Cumplimiento normativo NOM garantizado en cada lote',
      application:
        'Hospitales, clínicas, laboratorios de análisis',
      icon: 'ri-shield-check-line',
      image:
        'https://readdy.ai/api/search-image?query=medical%20grade%20chemical%20mixture%20healthcare%20professional%20white%20background%2C%20studio%20lighting%20minimalist%20corporate%20product%20photography%20premium%20medical%20supply%20elegant%20presentation%20no%20people&width=600&height=400&seq=mez-salud-003&orientation=landscape',
    },
    {
      name: 'Soluciones para Industria Alimenticia',
      description:
        'Productos de limpieza y sanitización con grado alimenticio para plantas procesadoras y cocinas industriales.',
      benefit:
        'Seguridad alimentaria certificada, sin riesgo de contaminación',
      application:
        'Plantas procesadoras, cocinas industriales, empacadoras',
      icon: 'ri-restaurant-line',
      image:
        'https://readdy.ai/api/search-image?query=food%20grade%20cleaning%20chemical%20solution%20professional%20white%20background%2C%20studio%20lighting%20minimalist%20corporate%20product%20photography%20premium%20food%20industry%20supply%20elegant%20presentation&width=600&height=400&seq=mez-alimentos-004&orientation=landscape',
    },
  ],
  industriales: [
    {
      name: 'Trapo Industrial',
      description:
        'Trapo industrial de alta absorción y resistencia para limpieza pesada en talleres, plantas y almacenes.',
      benefit:
        'Reutilizable, reduce costos vs. papel desechable en un 60%',
      application:
        'Talleres mecánicos, plantas industriales, almacenes',
      icon: 'ri-t-shirt-line',
      image:
        'https://readdy.ai/api/search-image?query=industrial%20cleaning%20rags%20cloths%20stack%20professional%20white%20background%2C%20studio%20lighting%20minimalist%20corporate%20product%20photography%20premium%20industrial%20supply%20elegant%20presentation%20no%20people&width=600&height=400&seq=ind-trapo-001&orientation=landscape',
    },
    {
      name: 'Cepillos Profesionales',
      description:
        'Línea completa de cepillos industriales: de mano, de piso, de tubería y especializados por sector.',
      benefit: 'Ergonomía certificada, mayor productividad del personal',
      application:
        'Industria alimenticia, manufactura, sector salud',
      icon: 'ri-brush-line',
      image:
        'https://readdy.ai/api/search-image?query=professional%20industrial%20cleaning%20brushes%20set%20various%20sizes%20white%20background%2C%20studio%20lighting%20minimalist%20corporate%20product%20photography%20premium%20cleaning%20tools%20elegant%20presentation&width=600&height=400&seq=ind-cepillo-002&orientation=landscape',
    },
    {
      name: 'Secadores de Aire',
      description:
        'Secadores de manos de alta velocidad con tecnología de bajo consumo energético para baños corporativos.',
      benefit:
        'Ahorro del 80% vs. toallas desechables a largo plazo',
      application:
        'Oficinas corporativas, hoteles, centros comerciales',
      icon: 'ri-windy-line',
      image:
        'https://readdy.ai/api/search-image?query=modern%20hand%20dryer%20air%20blower%20white%20professional%20background%2C%20studio%20lighting%20minimalist%20corporate%20product%20photography%20premium%20bathroom%20equipment%20elegant%20presentation%20no%20people&width=1400&height=400&seq=ind-secador-003&orientation=landscape',
    },
    {
      name: 'Sistemas de Aromatización',
      description:
        'Dispensadores automáticos programables para mantener ambientes frescos y agradables las 24 horas.',
      benefit:
        'Mejora la percepción del cliente en espacios comerciales',
      application:
        'Hoteles, centros comerciales, oficinas, restaurantes',
      icon: 'ri-mist-line',
      image:
        'https://readdy.ai/api/search-image?query=automatic%20air%20freshener%20dispenser%20system%20professional%20white%20background%2C%20studio%20lighting%20minimalist%20corporate%20product%20photography%20premium%20fragrance%20system%20elegant%20presentation&width=600&height=400&seq=ind-aroma-004&orientation=landscape',
    },
    {
      name: 'Sanitización por Goteo Programado',
      description:
        'Sistema automático de sanitización continua para baños de alto tráfico. Programable por horario y frecuencia.',
      benefit:
        'Higiene constante sin intervención manual del personal',
      application:
        'Baños públicos, hospitales, centros de convenciones',
      icon: 'ri-water-flash-line',
      image:
        'https://static.readdy.ai/image/31562a2a45c0a727692c38b1929c3137/2d06e580412e24ab1d4b5580918b3db2.png',
    },
    {
      name: 'Abrasivos y Discos de Corte',
      description:
        'Línea completa de abrasivos industriales, discos de corte, desbaste y pulido para metal, concreto y materiales diversos. Máxima durabilidad y rendimiento en operaciones de corte y acabado.',
      benefit: 'Mayor vida útil y cortes precisos que reducen tiempos de operación',
      application:
        'Talleres mecánicos, industria metalúrgica, construcción, manufactura',
      icon: 'ri-settings-5-line',
      image:
        'https://readdy.ai/api/search-image?query=industrial%20cutting%20discs%20and%20abrasive%20wheels%20arranged%20professionally%20on%20clean%20white%20background%2C%20professional%20studio%20lighting%20minimalist%20corporate%20product%20photography%20premium%20industrial%20tools%20elegant%20presentation%20no%20people&width=600&height=400&seq=ind-abrasivos-006&orientation=landscape',
    },
    {
      name: 'Laminado',
      description:
        'Películas y rollos de laminado industrial para protección de superficies, empaque y acabados profesionales. Disponible en diferentes calibres y presentaciones según la aplicación.',
      benefit: 'Protección superior de superficies y acabados con aplicación eficiente',
      application:
        'Industria del empaque, manufactura, artes gráficas, sector comercial',
      icon: 'ri-stack-line',
      image:
        'https://readdy.ai/api/search-image?query=industrial%20laminating%20film%20rolls%20and%20sheets%20arranged%20professionally%20on%20clean%20white%20background%2C%20professional%20studio%20lighting%20minimalist%20corporate%20product%20photography%20premium%20packaging%20supply%20elegant%20presentation%20no%20people&width=600&height=400&seq=ind-laminado-007&orientation=landscape',
    },
    {
      name: 'Maquinaria de Limpieza',
      description:
        'Equipos profesionales de limpieza industrial: fregadoras de piso, aspiradoras industriales, barredoras y pulidoras de alto rendimiento para grandes superficies.',
      benefit: 'Reduce hasta un 70% el tiempo de limpieza vs. métodos manuales',
      application:
        'Plantas industriales, centros comerciales, hospitales, almacenes',
      icon: 'ri-truck-line',
      image:
        'https://readdy.ai/api/search-image?query=professional%20industrial%20floor%20cleaning%20machine%20scrubber%20polisher%20on%20clean%20white%20background%2C%20professional%20studio%20lighting%20minimalist%20corporate%20product%20photography%20premium%20cleaning%20equipment%20elegant%20presentation%20no%20people&width=600&height=400&seq=ind-maquinaria-008&orientation=landscape',
    },
  ],
};

export default function Products() {
  const [activeCategory, setActiveCategory] = useState<CategoryId>('higienicos');
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  // Guard against missing data (robustness)
  const currentProducts = products[activeCategory] ?? [];
  const currentCategory = categories.find((c) => c.id === activeCategory);

  return (
    <section id="productos" className="py-28 bg-white" data-product-shop>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-20">
          <span className="inline-block text-xs font-semibold tracking-widest text-blue-700 uppercase mb-4 bg-blue-50 px-4 py-2 rounded-full">
            Catálogo Corporativo
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
            Líneas de Producto
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto leading-relaxed">
            Soluciones especializadas para cada sector industrial. Calidad certificada,
            entrega confiable y soporte técnico dedicado.
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-16">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id as CategoryId)}
              className={`flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer border ${
                activeCategory === cat.id
                  ? 'bg-blue-700 text-white border-blue-700 shadow-lg shadow-blue-200'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300 hover:text-blue-700'
              }`}
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className={`${cat.icon} text-base`}></i>
              </span>
              {cat.name}
            </button>
          ))}
        </div>

        {/* Category Label */}
        <div className="flex items-center gap-3 mb-10">
          <div className="w-10 h-10 flex items-center justify-center bg-blue-700 rounded-xl">
            <i className={`${currentCategory?.icon} text-white text-lg`}></i>
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">
              {currentCategory?.name}
            </h3>
            <p className="text-sm text-gray-400">
              {currentProducts.length} productos disponibles
            </p>
          </div>
          <div className="ml-auto hidden sm:block">
            <a
              href="https://wa.me/524492037021?text=Hola,%20me%20interesa%20cotizar%20productos%20de%20la%20categoría%20de%20"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-700 text-white font-semibold rounded-lg hover:bg-blue-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line"></i>
              Cotizar esta línea
            </a>
          </div>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {currentProducts.map((product, index) => (
            <div
              key={`${activeCategory}-${index}`}
              onMouseEnter={() => setHoveredCard(index)}
              onMouseLeave={() => setHoveredCard(null)}
              className="group bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-2xl hover:border-blue-100 transition-all duration-300 cursor-pointer"
            >
              {/* Image */}
              <div className="w-full h-48 overflow-hidden bg-gray-50">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>

              {/* Content */}
              <div className="p-5">
                <div className="w-9 h-9 flex items-center justify-center bg-blue-50 rounded-lg mb-3">
                  <i className={`${product.icon} text-blue-700 text-lg`}></i>
                </div>
                <h4 className="text-base font-bold text-gray-900 mb-2 leading-snug">
                  {product.name}
                </h4>
                <p className="text-gray-500 text-xs leading-relaxed mb-4">
                  {product.description}
                </p>

                {/* Benefit */}
                <div className="flex items-start gap-2 mb-3">
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                    <i className="ri-checkbox-circle-fill text-green-500 text-sm"></i>
                  </span>
                  <p className="text-xs text-gray-600 font-medium leading-snug">
                    {product.benefit}
                  </p>
                </div>

                {/* Application */}
                <div className="flex items-start gap-2">
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                    <i className="ri-building-2-line text-blue-400 text-sm"></i>
                  </span>
                  <p className="text-xs text-gray-400 leading-snug">
                    {product.application}
                  </p>
                </div>
              </div>

              {/* Hover CTA */}
              <div
                className={`px-5 pb-5 transition-all duration-300 ${
                  hoveredCard === index
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-2'
                }`}
              >
                <a
                  href={`https://wa.me/524492037021?text=Hola,%20me%20interesa%20cotizar:%20${encodeURIComponent(
                    product.name,
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center py-2.5 bg-blue-700 text-white text-xs font-semibold rounded-lg hover:bg-blue-800 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Solicitar cotización
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA Banner */}
        <div className="mt-20 rounded-3xl overflow-hidden relative">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=modern%20industrial%20warehouse%20clean%20organized%20shelves%20professional%20products%20storage%20facility%20wide%20angle%20shot%2C%20cool%20blue%20tones%2C%20high%20quality%20corporate%20photography%20no%20people%20bright%20lighting&width=1400&height=400&seq=products-cta-banner-001&orientation=landscape"
              alt="Catálogo completo HIPERCLEAN"
              className="w-full h-full object-cover object-top"
            />
            {/* Fixed gradient – removed the “/90” style which caused the TS parser error */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-900 via-blue-800 to-slate-900 opacity-70"></div>
          </div>

          <div className="relative z-10 px-10 py-14 flex flex-col sm:flex-row items-center justify-between gap-8">
            <div>
              <p className="text-blue-300 text-sm font-semibold tracking-widest uppercase mb-2">
                Catálogo Completo
              </p>
              <h3 className="text-3xl font-bold text-white mb-3">
                ¿Necesitas un producto específico?
              </h3>
              <p className="text-white/70 text-base max-w-xl">
                Contamos con más de 100 productos disponibles. Nuestro equipo
                te asesora para encontrar la solución exacta para tu empresa.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 shrink-0">
              <a
                href="tel:4499171543"
                className="flex items-center justify-center gap-2 px-7 py-4 bg-white text-blue-800 font-semibold rounded-xl hover:bg-blue-50 transition-colors shadow-lg whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line"></i>
                Llamar ahora
              </a>
              <a
                href="https://wa.me/524492037021?text=Hola,%20me%20interesa%20conocer%20el%20catálogo%20completo%20de%20HIPERCLEAN"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line"></i>
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
